/**
 * @fileoverview Página responsável por consolidar indicadores globais para visão executiva.
 */

import React, { useState, useEffect, useContext, useCallback, useRef } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  IconButton,
  Tooltip,
} from "@material-ui/core";
import CompaniesMultiSelect from "../../components/CompaniesMultiSelect";
import { makeStyles } from "@material-ui/core/styles";
import { blue, green, red } from "@material-ui/core/colors";
import FullscreenIcon from "@material-ui/icons/Fullscreen";
import FullscreenExitIcon from "@material-ui/icons/FullscreenExit";
import HourglassEmptyIcon from "@material-ui/icons/HourglassEmpty";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";

import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import { toast } from "react-toastify";
import moment from "moment";

import { RefreshCw, Users, MessageCircle, Timer } from "lucide-react";
// Adiciona a fonte Montserrat ao documento
const loadMontserratFont = () => {
  if (!document.getElementById('montserrat-font')) {
    const link = document.createElement('link');
    link.id = 'montserrat-font';
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800&display=swap';
    document.head.appendChild(link);
  }
};

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: "100vh",
    height: "100vh",
    overflow: "auto",
    background: theme.palette.type === "dark"
      ? "linear-gradient(135deg, #0a0a0b 0%, #121213 50%, #0a0a0b 100%)"
      : "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
    padding: theme.spacing(2),
    fontFamily: "'Montserrat', sans-serif !important",
    "& *": {
      fontFamily: "'Montserrat', sans-serif !important",
    },
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1.25),
    },
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(3),
    padding: theme.spacing(0, 1),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
      marginBottom: theme.spacing(2),
      padding: 0,
      gap: theme.spacing(1.25),
    },
  },
  title: {
    fontSize: "1.8rem",
    fontWeight: 700,
    color: theme.palette.type === "dark" ? "#ffffff" : "#1e293b",
    [theme.breakpoints.down("sm")]: {
      fontSize: "1.35rem",
    },
  },
  headerControls: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      flexWrap: "wrap",
      gap: theme.spacing(1),
    },
  },
  selectCompanies: {
    minWidth: 280,
    "& .MuiOutlinedInput-root": {
      borderRadius: 12,
    },
    [theme.breakpoints.down("sm")]: {
      minWidth: 0,
      flex: 1,
    },
  },
  companiesGrid: {
    display: "grid",
    gap: theme.spacing(2),
    paddingBottom: theme.spacing(4),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr !important",
      gap: theme.spacing(1.5),
    },
  },
  companiesGrid2Cols: {
    gridTemplateColumns: "repeat(2, 1fr)",
  },
  companiesGrid3Cols: {
    gridTemplateColumns: "repeat(3, 1fr)",
  },
  companyCard: {
    background: theme.palette.type === "dark"
      ? "linear-gradient(145deg, rgba(18, 18, 19, 0.9) 0%, rgba(26, 26, 28, 0.9) 100%)"
      : "#ffffff",
    borderRadius: 20,
    border: theme.palette.type === "dark"
      ? "1px solid rgba(91, 124, 255, 0.15)"
      : "1px solid #e2e8f0",
    boxShadow: theme.palette.type === "dark"
      ? "0 8px 32px rgba(0, 0, 0, 0.4)"
      : "0 4px 6px rgba(0, 0, 0, 0.05)",
    minHeight: 280,
    display: "flex",
    flexDirection: "column",
    transition: "all 0.3s ease",
    "&:hover": {
      border: theme.palette.type === "dark"
        ? "1px solid rgba(91, 124, 255, 0.3)"
        : "1px solid #cbd5e1",
      transform: "scale(1.005)",
    },
    [theme.breakpoints.down("sm")]: {
      minHeight: 0,
      borderRadius: 16,
    },
  },
  companyHeader: {
    padding: theme.spacing(2, 2.5),
    borderBottom: theme.palette.type === "dark"
      ? "1px solid rgba(255, 255, 255, 0.06)"
      : "1px solid #f1f5f9",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1.5, 2),
    },
  },
  companyName: {
    fontSize: "1.1rem",
    fontWeight: 600,
    color: theme.palette.type === "dark" ? "#ffffff" : "#1e293b",
    letterSpacing: "-0.02em",
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    [theme.breakpoints.down("sm")]: {
      fontSize: "1rem",
    },
  },
  attendantBadge: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    padding: "4px 10px",
    borderRadius: 20,
    background: theme.palette.type === "dark"
      ? "rgba(91, 124, 255, 0.15)"
      : "rgba(59, 130, 246, 0.1)",
    color: theme.palette.type === "dark" ? "#a5b4fc" : "#3b82f6",
    fontSize: "0.75rem",
    fontWeight: 500,
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
  metricsContainer: {
    flex: 1,
    padding: theme.spacing(2),
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: theme.spacing(1.5),
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1.25),
      gap: theme.spacing(1),
    },
  },
  metricCard: {
    background: theme.palette.type === "dark"
      ? "rgba(255, 255, 255, 0.03)"
      : "#f8fafc",
    borderRadius: 14,
    padding: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    transition: "all 0.2s ease",
    border: theme.palette.type === "dark"
      ? "1px solid rgba(255, 255, 255, 0.04)"
      : "1px solid transparent",
    "&:hover": {
      background: theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.05)"
        : "#f1f5f9",
    },
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1.5, 1),
      borderRadius: 12,
    },
  },
  metricCardFull: {
    gridColumn: "1 / -1",
  },
  metricIcon: {
    marginBottom: 6,
    opacity: 0.9,
  },
  metricValue: {
    fontSize: "2rem",
    fontWeight: 700,
    lineHeight: 1,
    marginBottom: 4,
    [theme.breakpoints.down("sm")]: {
      fontSize: "1.6rem",
    },
  },
  metricLabel: {
    fontSize: "0.7rem",
    fontWeight: 500,
    color: theme.palette.type === "dark" ? "#9ca3af" : "#64748b",
    textAlign: "center",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
  metricSubtext: {
    fontSize: "0.6rem",
    color: theme.palette.type === "dark" ? "#6b7280" : "#94a3b8",
    marginTop: 2,
  },
  percentageBadge: {
    fontSize: "1.65rem",
    fontWeight: 600,
    padding: "4px 10px",
    borderRadius: 8,
    marginTop: 4,
  },
  refreshTime: {
    fontSize: "0.75rem",
    color: theme.palette.type === "dark" ? "#6b7280" : "#94a3b8",
    marginRight: theme.spacing(1),
    whiteSpace: "nowrap",
    [theme.breakpoints.down("sm")]: {
      marginRight: 0,
      flexBasis: "100%",
      order: -1,
    },
  },
  fullscreenMode: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 9999,
    background: theme.palette.type === "dark"
      ? "linear-gradient(135deg, #0a0a0b 0%, #121213 50%, #0a0a0b 100%)"
      : "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
    padding: theme.spacing(2),
  },
}));

const SuperDashboard = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const [companies, setCompanies] = useState([]);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [prefsLoaded, setPrefsLoaded] = useState(false);
  const [dashboardData, setDashboardData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const savePrefsTimer = useRef(null);

  // Carregar fonte Montserrat
  useEffect(() => {
    loadMontserratFont();
  }, []);

  // Carregar lista de empresas + preferências do usuário em paralelo
  useEffect(() => {
    const hydrate = async () => {
      try {
        const [{ data: companiesList }, { data: me }] = await Promise.all([
          api.get("/super-dashboard/companies"),
          api.get("/auth/me"),
        ]);
        setCompanies(companiesList);

        const savedIds = me?.uiPreferences?.superDashboardCompanies;
        const validSaved = Array.isArray(savedIds)
          ? savedIds.filter((id) => companiesList.some((c) => c.id === id))
          : [];

        if (validSaved.length > 0) {
          setSelectedCompanies(validSaved);
        } else if (companiesList.length > 0) {
          setSelectedCompanies(companiesList.slice(0, 4).map((c) => c.id));
        }
      } catch (err) {
        console.error("Erro ao carregar dados iniciais:", err);
        toast.error("Erro ao carregar lista de empresas");
      } finally {
        setPrefsLoaded(true);
      }
    };
    hydrate();
  }, []);

  // Persiste seleção no backend (debounced) após hidratação inicial
  const handleSelectedCompaniesChange = useCallback((next) => {
    setSelectedCompanies(next);
    if (savePrefsTimer.current) clearTimeout(savePrefsTimer.current);
    savePrefsTimer.current = setTimeout(() => {
      api
        .patch("/auth/me/ui-preferences", { superDashboardCompanies: next })
        .catch((err) => console.error("Erro ao salvar preferências:", err));
    }, 600);
  }, []);

  useEffect(() => {
    return () => {
      if (savePrefsTimer.current) clearTimeout(savePrefsTimer.current);
    };
  }, []);

  // Buscar dados do dashboard
  const fetchDashboardData = useCallback(async () => {
    if (selectedCompanies.length === 0) return;

    setLoading(true);
    try {
      const { data } = await api.get("/super-dashboard", {
        params: {
          companyIds: selectedCompanies.join(","),
          tz: moment().format("Z"),
        }
      });
      setDashboardData(data);
      setLastUpdate(new Date());
    } catch (err) {
      console.error("Erro ao buscar dados:", err);
      toast.error("Erro ao buscar dados do dashboard");
    }
    setLoading(false);
  }, [selectedCompanies]);

  // Buscar dados quando empresas selecionadas mudarem
  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Auto-refresh a cada 30 segundos
  useEffect(() => {
    const interval = setInterval(() => {
      fetchDashboardData();
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchDashboardData]);

  // Toggle fullscreen
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  // Listener para escape do fullscreen
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);


  // Determina o número de colunas baseado na quantidade de empresas
  const getGridClass = () => {
    if (selectedCompanies.length <= 4) {
      return classes.companiesGrid2Cols;
    }
    return classes.companiesGrid3Cols;
  };

  // Paleta de criticidade para a taxa "Sem Resp. +10min" sobre os ativos.
  // Faixas: <15% verde escuro, 15-25% verde claro, 25-30% laranja, ≥30% vermelho.
  const getRateColor = (rate) => {
    if (rate < 15) return "#15803d";  // green-700
    if (rate < 25) return "#22c55e";  // green-500
    if (rate < 30) return "#f97316";  // orange-500
    return "#ef4444";                  // red-500
  };

  const ART_GREEN = "#22c55e";
  const ART_YELLOW = "#f59e0b";
  const ART_RED = "#ef4444";
  const ART_MUTED = "#64748b";
  const formatARTMinutes = (m) => {
    if (m === null || m === undefined || Number.isNaN(m)) return "—";
    const min = Math.floor(m);
    if (min < 60) return min + "min";
    const h = Math.floor(min / 60);
    const rest = min - h * 60;
    return rest === 0 ? h + "h" : h + "h" + rest + "min";
  };
  const artColorForStatus = (status, avgMinutes) => {
    if (status === "green") return ART_GREEN;
    if (status === "yellow") return ART_YELLOW;
    if (status === "red") return ART_RED;
    return ART_MUTED;
  };

  const MetricCard = ({ icon: Icon, value, label, color, subtext, percentage, fullWidth }) => {
    // Quando o card expõe um percentual (ex.: "Sem Resp. +10min" usa a taxa
    // sobre os ativos), o tom é escolhido pela faixa de criticidade — abaixo
    // a cor recebida via prop só é usada como fallback (cards sem percentage).
    const effectiveColor = percentage !== undefined ? getRateColor(percentage) : color;
    return (
      <Box className={`${classes.metricCard} ${fullWidth ? classes.metricCardFull : ""}`}>
        <Icon className={classes.metricIcon} style={{ color: effectiveColor, fontSize: "1.5rem" }} />
        <Typography className={classes.metricValue} style={{ color: effectiveColor }}>
          {value}
        </Typography>
        <Typography className={classes.metricLabel}>{label}</Typography>
        {subtext && (
          <Typography className={classes.metricSubtext}>{subtext}</Typography>
        )}
        {percentage !== undefined && percentage > 0 && (
          <Box
            className={classes.percentageBadge}
            style={{
              background: `${effectiveColor}20`,
              color: effectiveColor,
            }}
          >
            {percentage}% dos ativos
          </Box>
        )}
      </Box>
    );
  };

  const CompanyCard = ({ company }) => (
    <Card className={classes.companyCard}>
      <Box className={classes.companyHeader}>
        <Typography className={classes.companyName}>{company.companyName}</Typography>
        <Box className={classes.attendantBadge}>
          <Users size={14} />
          {company.attendantCount} atendentes
        </Box>
      </Box>
      <Box className={classes.metricsContainer}>
        <MetricCard
          icon={MessageCircle}
          value={company.activeConversations}
          label="Ativos"
          color={blue[500]}
          subtext="(24h)"
        />
        <MetricCard
          icon={TrendingUpIcon}
          value={company.avgPerAttendant}
          label="Média/Atendente"
          color={green[500]}
        />
        <MetricCard
          icon={Timer}
          value={formatARTMinutes(company.art?.avgMinutes ?? null)}
          label="ART (24h)"
          color={artColorForStatus(company.art?.status || "empty", company.art?.avgMinutes ?? null)}
          subtext={
            company.art?.scheduleConfigured === false
              ? "sem horário"
              : (company.art?.pairCount || 0) > 0
                ? `${company.art.pairCount} pares`
                : "sem dados"
          }
        />
        <MetricCard
          icon={HourglassEmptyIcon}
          value={company.noResponse}
          label="Sem Resp. +10min"
          color={red[500]}
          percentage={company.noResponseRate}
        />
      </Box>
    </Card>
  );

  // Verificar se é superadmin
  if (!user?.super) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <Typography variant="h6">Acesso não autorizado</Typography>
      </Box>
    );
  }

  return (
    <Box className={`${classes.root} ${isFullscreen ? classes.fullscreenMode : ""}`}>
      {/* Header */}
      <Box className={classes.header}>
        <Typography className={classes.title}>
          Super Dashboard
        </Typography>
        <Box className={classes.headerControls}>
          {lastUpdate && (
            <Typography className={classes.refreshTime}>
              Atualizado: {lastUpdate.toLocaleTimeString("pt-BR")}
            </Typography>
          )}
          <CompaniesMultiSelect
            companies={companies}
            selectedIds={selectedCompanies}
            onChange={handleSelectedCompaniesChange}
            disabled={!prefsLoaded}
          />
          <Tooltip title="Atualizar">
            <IconButton onClick={fetchDashboardData} disabled={loading}>
              <RefreshCw style={{ animation: loading ? "spin 1s linear infinite" : "none" }} />
            </IconButton>
          </Tooltip>
          <Tooltip title={isFullscreen ? "Sair do fullscreen" : "Modo TV (fullscreen)"}>
            <IconButton onClick={toggleFullscreen}>
              {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Grid de Empresas */}
      <Box className={`${classes.companiesGrid} ${getGridClass()}`}>
        {dashboardData.map((company) => (
          <CompanyCard key={company.companyId} company={company} />
        ))}
      </Box>

      {/* CSS para animação do spinner */}
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </Box>
  );
};

export default SuperDashboard;
